import React from "react";
import { isPriceAgeWarning } from "./composerModel";

/**
 * Why the AI drafted these numbers — the inspector's top section.
 *
 * Two rules govern this panel:
 *
 *   NO DOLLARS ON EVIDENCE. The evidence rows carry a `unit_cost` from
 *   decades-old spreadsheets and the panel deliberately does not render it.
 *   This surface justifies INPUTS — which stock, how many parts, how many
 *   hours. Every dollar in the workbench comes from /api/calculate, and a
 *   sheet price from 2014 sitting next to a live draft would read as a quote.
 *   The exception is a server-authored price-age warning: when a draft
 *   component copied a historical unit_cost verbatim, that copied figure is
 *   named so the estimator can check the current catalog before quoting.
 *
 *   TEXT, NEVER MARKUP. Every string here originates from a language model or
 *   from a legacy spreadsheet cell. They render as text nodes — no
 *   dangerouslySetInnerHTML, no markdown, no auto-linkifying — because
 *   `evidence[].description` is exactly where injection text aimed at the
 *   model would sit, and on screen it is just a string.
 */

function Section({ title, children }) {
  return (
    <section className="est-composer-panel__sec">
      <h3 className="est-composer-panel__h">{title}</h3>
      {children}
    </section>
  );
}

/** A cited job, resolved from the evidence list by id. */
function EvidenceChips({ ids = [], byId }) {
  const chips = ids.map((id) => byId[id]).filter(Boolean);
  if (!chips.length) return null;
  return (
    <span className="est-composer-panel__chips">
      {chips.map((row) => (
        <span
          key={row.id}
          className="est-composer-panel__chip"
          title={row.name}
        >
          {row.job_number || `#${row.id}`}
        </span>
      ))}
    </span>
  );
}

function hoursText(value) {
  return value === null || value === undefined ? null : `${value} hr`;
}

function ComposerReviewPanel({ draft }) {
  if (!draft) return null;

  const {
    decomposition,
    rationale = [],
    evidence = [],
    confidence = [],
    warnings = [],
    model,
    generatedAt,
    manufacturingVersion,
  } = draft;

  const evidenceById = {};
  evidence.forEach((row) => {
    evidenceById[row.id] = row;
  });

  return (
    <details className="est-composer-panel" open>
      <summary className="est-composer-panel__summary">
        <span className="est-composer-panel__badge">AI-drafted</span>
        How this line was drafted
      </summary>

      <div className="est-composer-panel__body">
        {decomposition ? (
          <Section title="How it's built">
            <p className="est-composer-panel__lead">
              {[decomposition.classificationPhrase, decomposition.family]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {decomposition.method_summary ? (
              <p className="est-composer-panel__note">
                {decomposition.method_summary}
              </p>
            ) : null}
            {decomposition.parts.length ? (
              <ul className="est-composer-panel__parts">
                {decomposition.parts.map((part, index) => (
                  <li key={`${part.role}-${index}`}>
                    <b>{part.role}</b>
                    {part.stock ? <span> · {part.stock}</span> : null}
                    {part.note ? (
                      <em className="est-composer-panel__note">{part.note}</em>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </Section>
        ) : null}

        {rationale.length ? (
          <Section title="Why these numbers">
            <ul className="est-composer-panel__rationale">
              {rationale.map((row, index) => (
                <li key={`${row.field || "draft"}-${index}`}>
                  {row.field ? (
                    <b className="est-composer-panel__field">{row.field}</b>
                  ) : null}
                  <span>{row.statement}</span>
                  <EvidenceChips ids={row.evidence_ids} byId={evidenceById} />
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {evidence.length ? (
          <Section title="Similar past work">
            <ul className="est-composer-panel__evidence">
              {evidence.map((row) => (
                <li key={row.id}>
                  <div className="est-composer-panel__ev-hd">
                    <b>{row.job_number || `#${row.id}`}</b>
                    <span>{row.name || row.description || "Line item"}</span>
                  </div>
                  <div className="est-composer-panel__ev-meta">
                    {[row.dimensions_raw, row.material_type]
                      .filter(Boolean)
                      .join(" · ") || "No dimensions recorded"}
                  </div>
                  {/* Hours only — never the sheet's price. */}
                  <div className="est-composer-panel__ev-hours">
                    {[
                      ["Per unit", hoursText(row.labor_per_unit)],
                      ["CNC", hoursText(row.cnc_hours)],
                      ["Cut", hoursText(row.cut_hours)],
                      ["Fab", hoursText(row.fab_hours)],
                      ["Paint", hoursText(row.paint_hours)],
                    ]
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <span key={label}>
                          <em>{label}</em> {value}
                        </span>
                      ))}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {confidence.length ? (
          <Section title="Confidence">
            <div className="est-composer-panel__conf">
              {confidence.map((entry) => (
                <span
                  key={entry.field}
                  className={`est-composer-panel__conf-chip est-composer-panel__conf-chip--${entry.level}`}
                  title={entry.reason || undefined}
                >
                  {entry.field} · {entry.level}
                </span>
              ))}
            </div>
          </Section>
        ) : null}

        {warnings.length ? (
          <Section title="Worth checking">
            <ul className="est-composer-panel__warnings" role="status">
              {warnings.map((warning) => (
                <li
                  key={warning}
                  data-kind={
                    isPriceAgeWarning(warning) ? "price-age" : undefined
                  }
                >
                  {warning}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        <p className="est-composer-panel__audit">
          {[
            model,
            generatedAt,
            manufacturingVersion ? `shop manual ${manufacturingVersion}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
    </details>
  );
}

export default ComposerReviewPanel;
